import { Button, Space } from "antd";
import type { HookAPI } from "antd/es/modal/useModal";
import type { SortOrder } from "antd/es/table/interface";
import type { ColumnType } from "antd/lib/table";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import CustomSelect from "~components/CustomSelect";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { ImageSizes } from "~enums/imageSizes.ts";
import { ImageTypes } from "~enums/imageTypes.ts";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import type { RoleStatuses } from "~enums/roleStatuses";
import type { Roles } from "~enums/roles";
import type { UpdateRole } from "~hooks/apiHooks/useUpdateRole";
import { getRoleTranslationKey } from "~utilities/getRoleTranslationKey";
import classNames from "~utils/classNames";
import ImageView from "../../../../components/ImageView/ImageView.tsx";
import styles from "../SeasonOfficialsPage.module.css";
import type { SeasonOfficialsTableRow } from "../types/seasonOfficialsTableRow";

export const useOfficialsColumns = (
  officialRoles: Roles[],
  statuses: RoleStatuses[],
  isMassEdit: boolean,
  updateRole: (payload: UpdateRole) => void,
  onDelete: (roleId: string) => void,
  modal: HookAPI,
) => {
  const { formatMessage } = useIntl();
  const { sport = "", customerId, organizationId } = useParams();

  const [editingKey, setEditingKey] = useState<string | null>(null);
  // biome-ignore lint: biome is wrong, it needs to be triggered on isMassEdit change
  useEffect(() => void setEditingKey(null), [isMassEdit]);

  const { hasPermission } = useUserPermissions();
  const editRoleAllowed = hasPermission(PermissionNames.ROLES, PermissionOperations.UPDATE);
  const editPersonAllowed = hasPermission(PermissionNames.PERSON, PermissionOperations.UPDATE);
  const deleteRoleAllowed = hasPermission(PermissionNames.ROLES, PermissionOperations.DELETE);

  const columns: ColumnType<SeasonOfficialsTableRow>[] = [
    {
      title: formatMessage({ id: "name.local.full" }),
      dataIndex: "nameFullLocal",
      render: (value, row) => (
        <Space size="small">
          <ImageView imageUrl={row.avatarUrl} size={ImageSizes.Small} width={25} height={25} imageType={ImageTypes.PersonHead} />
          {value}
        </Space>
      ),
    },
    {
      title: formatMessage({ id: "nationality" }),
      dataIndex: "nationality",
      width: 60,
    },
    {
      title: formatMessage({ id: "gender" }),
      dataIndex: "gender",
      width: 60,
    },
    {
      title: formatMessage({ id: "role" }),
      dataIndex: "role",
      width: 280,
      sorter: true,
      defaultSortOrder: "ascend" as SortOrder,
      sortDirections: ["ascend", "descend", "ascend"] as SortOrder[],
      render: (role, record) =>
        isMassEdit || record.roleId === editingKey ? (
          <CustomSelect
            value={record.role}
            options={officialRoles.map((role) => ({
              label: formatMessage({
                id: `roles.${getRoleTranslationKey(role, sport)}`,
              }),
              value: role,
            }))}
            onChange={(value) => updateRole({ roleId: record.roleId, role: value as Roles })}
            className={styles.rolesSelect}
            showSearch
          />
        ) : (
          formatMessage({
            id: `roles.${getRoleTranslationKey(role, sport)}`,
          })
        ),
      onCell: (record) => ({
        isEditing: isMassEdit || record.roleId === editingKey,
        title: formatMessage({ id: "role" }),
      }),
    },
    {
      title: formatMessage({ id: "status" }),
      width: 140,
      dataIndex: "status",
      render: (status, record) =>
        isMassEdit || record.roleId === editingKey ? (
          <CustomSelect
            value={record.status}
            options={statuses.map((status) => ({
              label: formatMessage({ id: `roleStatus.${status}` }),
              value: status,
            }))}
            onChange={(value) =>
              updateRole({
                roleId: record.roleId,
                status: value as RoleStatuses,
              })
            }
            className={styles.select}
          />
        ) : status ? (
          formatMessage({ id: `roleStatus.${status}` })
        ) : (
          ""
        ),
      onCell: (record) => ({
        isEditing: isMassEdit || record.roleId === editingKey,
        title: formatMessage({ id: "status" }),
      }),
    },
    {
      align: "right",
      width: 100,
      render: (_value, row) => {
        const isEditing = row.roleId === editingKey;
        return (
          <>
            {editRoleAllowed && (
              <Button
                type="text"
                size="small"
                title={formatMessage({
                  id: "fixtureListPage.table.column.actions.inlineEdit",
                })}
                icon={<i className={isEditing ? "fas fa-pen-clip" : "fas fa-pen"} />}
                onClick={() => setEditingKey(isEditing ? null : row.roleId)}
                className={classNames(isMassEdit && styles.hidden)}
              />
            )}
            {editPersonAllowed && row.personId && (
              <Link to={`/${customerId}/admin/${sport}/${organizationId}/persons/manage/${row.personId}`}>
                <Button type="text" size="small" title={formatMessage({ id: "manage" })} icon={<i className="fas fa-cog" />} />
              </Link>
            )}
            {deleteRoleAllowed && (
              <Button
                type="text"
                size="small"
                icon={<i className="fas fa-trash-can mr-2" />}
                onClick={() => {
                  modal.confirm({
                    title: formatMessage({
                      id: "officials.delete.confirm.title",
                    }),
                    content: formatMessage({ id: "delete.item.confirm" }),
                    okText: formatMessage({ id: "delete" }),
                    onOk: () => {
                      onDelete(row.roleId);
                    },
                  });
                }}
              />
            )}
          </>
        );
      },
    },
  ];

  return columns;
};
